import { initializeApp } from "firebase/app";
import {
  deleteToken,
  getMessaging,
  getToken,
  type Messaging,
} from "firebase/messaging";
import { fetchClient } from "@/api-client";
import type { NotificationOutput } from "@/api-types";
import { config } from "@/config";

// Function to create a simple hash of the token
const hashToken = async (token: string): Promise<string> => {
  const encoder = new TextEncoder();
  const data = encoder.encode(token);
  const hashBuffer = await crypto.subtle.digest("SHA-256", data);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  return hashArray.map((b) => b.toString(16).padStart(2, "0")).join("");
};

// Function to get stored token hash from localStorage
const getStoredTokenHash = (): string | null =>
  localStorage.getItem("fcm_token_hash");

// Function to save token hash to localStorage
const saveTokenHash = (hash: string): void =>
  localStorage.setItem("fcm_token_hash", hash);

import {
  type NotificationData,
  notifications,
  notificationsStore,
} from "@mantine/notifications";
import { fetchAuthSession } from "aws-amplify/auth";
import { idTokenToUserInfo } from "@/UserContext";
import {
  getNotificationIcon,
  renderNotificationLink,
  renderNotificationMessage,
} from "@/utils/notifications";

export const displayNotification = (notification: NotificationOutput) => {
  const params = {
    id: notification.id,
    message: renderNotificationMessage(notification),
    onClick: () => {
      if (notification.role) {
        window.location.href = renderNotificationLink({
          notification,
        });
      }
    },
    withCloseButton: true,
    position: "top-center",
    autoClose: false,
    icon: getNotificationIcon(notification.type),
  } as NotificationData;

  const notificationsState = notificationsStore.getState();
  const existingNotification = notificationsState.notifications.find(
    (n) => n.id === notification.id,
  );

  if (existingNotification) {
    notifications.update(params);
  } else {
    notifications.show(params);
  }
};

// Class to manage FCM setup and token hash
export class FCMManager {
  private static instance: FCMManager | null = null;
  private messaging: Messaging;
  private token: string;

  private constructor(messaging: Messaging, token: string) {
    this.messaging = messaging;
    this.token = token;
  }

  private static async createInstance(firebaseConfig: object) {
    console.log("Creating FCM instance");

    const app = initializeApp(firebaseConfig);
    const messaging = getMessaging(app);

    const getSwRegistration = async () =>
      await navigator.serviceWorker.getRegistration("/sw.js");
    let swRegistration = await getSwRegistration();
    for (let attempt = 1; attempt <= 10 && !swRegistration; attempt++) {
      if (attempt > 1) {
        console.log(`Service worker registration attempt ${attempt}/10`);
        await new Promise((resolve) => setTimeout(resolve, 1000));
      }
      swRegistration = await getSwRegistration();
    }
    if (!swRegistration) {
      console.error("Service worker not registered after 10 attempts/seconds");
      return null;
    }
    const token = await getToken(messaging, {
      serviceWorkerRegistration: swRegistration,
    });
    if (!token) {
      console.error("Failed to get FCM token");
      return null;
    }
    const tokenHash = await hashToken(token);
    const storedHash = getStoredTokenHash();

    if (tokenHash !== storedHash) {
      await fetchClient.POST("/universal/fcm-token", {
        body: {
          token,
          platform: "WEB",
        },
      });
      saveTokenHash(tokenHash);
    }

    return new FCMManager(messaging, token);
  }

  private async revokeToken() {
    await deleteToken(this.messaging);
    localStorage.removeItem("fcm_token_hash");
  }

  public static async init() {
    if (FCMManager.instance) {
      return;
    }

    FCMManager.instance = await FCMManager.createInstance(
      config.firebaseConfig,
    );
  }

  public static async revoke() {
    if (!FCMManager.instance) {
      return;
    }
    await FCMManager.instance.revokeToken();
    FCMManager.instance = null;
  }
}

// Passive version: checks current notification permission and proceeds if granted
export const initFCMPassive = async () => {
  const session = await fetchAuthSession();
  const userInfo =
    session.tokens?.idToken?.payload &&
    idTokenToUserInfo(session.tokens?.idToken?.payload);
  if (!userInfo?.accountId) {
    return;
  }

  const permission = Notification.permission;
  if (permission !== "granted") {
    return;
  }
  await FCMManager.init();
};

// Active version: requests notification permission and proceeds if granted
export const initFCMActive = async () => {
  const permission = await Notification.requestPermission();
  if (permission !== "granted") {
    console.log("Notification permission denied, skipping FCM setup");
    return;
  }
  await FCMManager.init();
};
